// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Loader, Text, View } from 'reshaped';

import { useEntityPermissions } from '@gandi/acl';
import {
  contactbook,
  type FavoriteContact,
} from '@gandi/redux-resource-contactbook';
import { sendPage } from '@gandi/tracking';
import { useTranslator } from '@gandi/translate';

import { Layout } from 'src/components/Layout';
import type { Params, Query } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';
import type { PreFetchArgs, PreFetchReturn, FullState } from 'src/types';

import { getBaseContact } from '../../DomainContacts/Wizard/modules/actions';

import {
  getDisplayedRole,
  getDisplayedDomainProduct,
} from '../modules/helpers';

import { selectSetupOwner } from '../modules/selectors';

import type { CreateOrTransfer, EditableRole } from '../types';

import Create from './components/Create';
import Edit from './components/Edit';

import Select from './components/Select';
import { selectContacts } from './modules/selectors';

const fetchProps = {};

function Presenter(): React.Node {
  const translator = useTranslator();
  const dispatch = useDispatch();

  const { process, role } = useParams<{
    cartId: string,
    process: CreateOrTransfer,
    role: EditableRole,
  }>();

  const [owner, setOwner] = React.useState<void | FavoriteContact>();
  const [mode, setMode] = React.useState('modify');

  const contactbookOrgId = useAppSelector(selectSetupOwner);

  const { hasPermissions, isLoadedPermissions } =
    useEntityPermissions(contactbookOrgId);

  const canReadContactBook = hasPermissions(['organizations:contactbook:read']);
  const canWriteContactBook = hasPermissions([
    'organizations:contactbook:write',
  ]);

  const contacts = useAppSelector(state =>
    selectContacts(state, contactbookOrgId),
  );
  const isSelectEnabled = canReadContactBook && contacts && !!contacts.length;

  const isFetching = useAppSelector(state =>
    contactbook.selectIsFetching(state, contactbookOrgId, fetchProps),
  );
  const isContactListLoaded = useAppSelector(state =>
    contactbook.selectIsLoaded(state, contactbookOrgId, fetchProps),
  );
  const isContactListFailure = useAppSelector(state =>
    contactbook.selectIsFailure(state, contactbookOrgId, fetchProps),
  );
  const isContactListToRefetch = useAppSelector(state =>
    contactbook.selectIsToRefetch(state, contactbookOrgId, fetchProps),
  );

  const shouldFetchContactList =
    !isFetching &&
    ((!isContactListLoaded && !isContactListFailure) || isContactListToRefetch);

  React.useEffect(() => {
    (async function asyncLoader() {
      if (!owner) {
        const baseContact = await dispatch(getBaseContact(translator));
        // $FlowFixMe[incompatible-call]: TODO
        setOwner(baseContact);
      }
    })();
  }, [dispatch, owner, setOwner, translator]);

  React.useEffect(() => {
    if (shouldFetchContactList) {
      dispatch(contactbook.fetchContactList(contactbookOrgId, {}));
    }
  }, [contactbookOrgId, dispatch, shouldFetchContactList]);

  React.useEffect(() => {
    if (role) {
      return sendPage({
        chapter1: 'Shop',
        chapter2: 'Configure',
        chapter3: 'role_contact',
        name: `domain${process}-${role}-${mode}`,
      });
    }
    return undefined;
  }, [process, role, mode]);

  const { __ } = translator;

  const contactRole = getDisplayedRole(translator, role);
  const domainProcess = getDisplayedDomainProduct(translator, process);

  const title =
    domainProcess &&
    __('%(domainProcess)s for the entire cart.', { domainProcess });
  const subTitle = __('%(contactRole)s selection.', { contactRole });

  const isLoaded =
    !isFetching &&
    isLoadedPermissions &&
    (isContactListLoaded || !shouldFetchContactList) &&
    !!owner;

  const modesTypes = [
    { label: __('Edit Contact'), value: 'modify' },
    { label: __('Select Contact'), value: 'select' },
    { label: __('Create Contact'), value: 'create' },
  ];

  return (
    <Layout currentStep={1} backgroundColor="surface">
      <Text as="h1" variant="featured-2" weight="bold">
        {title}
      </Text>
      <Text
        as="h2"
        variant="featured-3"
        weight="bold"
        attributes={{ style: { marginBottom: 'var(--rs-unit-x5)' } }}
      >
        {subTitle}
      </Text>
      {!isLoaded && (
        <View justify="center" direction="row">
          <Loader size="large" ariaLabel={__('Loading...')} />
        </View>
      )}
      {isLoaded && (
        <View gap={5}>
          <View gap={1}>
            <Text variant="body-2" weight="medium">
              {__('What would you like to do?')}
            </Text>
            <View gap={3} direction="row" paddingBlock={1}>
              {modesTypes.map(processType => {
                const isSelected = mode === processType.value;

                if (!isSelectEnabled && mode === 'select') return null;

                return (
                  <Button
                    rounded
                    key={processType.value}
                    color={isSelected ? 'primary' : undefined}
                    variant={isSelected ? 'solid' : 'outline'}
                    onClick={() => setMode(processType.value)}
                  >
                    {processType.label}
                  </Button>
                );
              })}
            </View>
          </View>
          {mode === 'modify' && (
            <Edit
              process={process}
              role={role}
              canWriteContactBook={canWriteContactBook}
              owner={owner}
            />
          )}
          {mode === 'select' && isSelectEnabled && (
            <Select process={process} role={role} contacts={contacts} />
          )}
          {mode === 'create' && (
            <Create canWriteContactBook={canWriteContactBook} />
          )}
        </View>
      )}
    </Layout>
  );
}

Presenter.preFetch = async (
  args: PreFetchArgs<{}, Params, Query, FullState>,
): PreFetchReturn<> => {
  const { dispatch, getState } = args;
  const contactbookOrgId = selectSetupOwner(getState());

  return Promise.allSettled([
    Select.preFetch(args),
    dispatch(contactbook.fetchContactList(contactbookOrgId, {})),
  ]);
};

export default Presenter;
